// Réplica visual simplificada do cardápio do cliente (packages/menu), usada para
// dar preview das cores do formulário de branding antes de salvar em produção.
// Os valores de gradiente/raio/sombra abaixo espelham os usados em
// packages/menu/src/components/MenuApp.tsx — mudanças visuais lá devem ser
// refletidas aqui também.
import { intlLocaleFor } from '@self-restaurant/shared'

export type MenuThemeColors = {
  background: string
  header: string
  button: string
  buttonActive: string
  selected: string
}

export function MenuThemePreview({
  colors,
  restaurantName,
  currency = 'BRL',
  countryCode,
}: {
  colors: MenuThemeColors
  restaurantName: string
  /** #428: moeda do país do tenant e locale de formatação ({samplePrice} → 42 € em PT) */
  currency?: string | null
  countryCode?: string | null
}) {
  const { background, header, button, buttonActive, selected } = colors
  const samplePrice = new Intl.NumberFormat(intlLocaleFor('pt', countryCode), {
    style: 'currency',
    currency: currency ?? 'BRL',
    minimumFractionDigits: 0,
  }).format(42)

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      <style>{`.theme-preview-btn:active { background: ${buttonActive} !important; }`}</style>

      {/* Cabeçalho */}
      <div
        style={{
          background: [
            'linear-gradient(180deg, rgba(0,0,0,.2), rgba(0,0,0,.55))',
            'radial-gradient(circle at 20% 10%, rgba(255,255,255,.25), transparent 28%)',
            `linear-gradient(135deg, #283521, ${header} 48%, #E5C59C)`,
          ].join(', '),
        }}
        className="px-4 pt-5 pb-8 text-white"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/25 flex-shrink-0" />
          <span className="font-serif text-lg">{restaurantName || 'Meu Restaurante'}</span>
        </div>
      </div>

      {/* Corpo */}
      <div style={{ background }} className="p-4 space-y-4">
        {/* Chips de categoria */}
        <div className="flex gap-2">
          <span
            style={{ background: selected, borderColor: selected }}
            className="px-3 py-1.5 rounded-full text-xs font-bold text-white border"
          >
            Entradas
          </span>
          <span className="px-3 py-1.5 rounded-full text-xs font-bold text-gray-500 bg-white border border-gray-200">
            Pratos
          </span>
          <span className="px-3 py-1.5 rounded-full text-xs font-bold text-gray-500 bg-white border border-gray-200">
            Sobremesas
          </span>
        </div>

        {/* Card de produto */}
        <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 p-3">
          <div className="w-14 h-14 rounded-lg flex-shrink-0 bg-gradient-to-br from-[#c57a42] via-[#f1c985] to-[#43502f]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">Prato exemplo</p>
            <p className="text-xs text-gray-500">Descrição curta do produto</p>
          </div>
          <span style={{ color: button }} className="text-sm font-bold flex-shrink-0">
            {samplePrice}
          </span>
        </div>

        {/* Variação selecionada */}
        <div className="flex gap-2">
          <span
            style={{ background: selected, borderColor: selected }}
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-white border"
          >
            Média
          </span>
          <span className="px-3 py-1.5 rounded-full text-xs font-semibold text-gray-700 border border-gray-200">
            Grande
          </span>
        </div>

        {/* CTA — clique e segure para ver a cor de "pressionado" */}
        <button
          style={{ background: button }}
          className="theme-preview-btn w-full rounded-full py-3 text-sm font-bold text-white text-center"
        >
          Adicionar ao pedido — {samplePrice}
        </button>

        {/* Barra flutuante inferior */}
        <div className="flex justify-center pt-2">
          <div className="flex gap-6 bg-white/90 border border-gray-200 rounded-full px-6 py-2.5 shadow-md">
            <span style={{ color: selected }} className="text-xs font-semibold">
              ⌂ Início
            </span>
            <span className="text-xs font-semibold text-gray-400">🛒 Carrinho</span>
          </div>
        </div>
      </div>
    </div>
  )
}
